import { LitElement, css, html, classMap, styleMap } from 'lit-all'

import styles from 'lib-styles'
import * as paths from 'lib-paths'
import { formatMoney, handleize } from 'lib-utils'

import { addToCart } from 'lib-utils'

const quickShopState = {
  initial: 'SHOW_OPTION_SELECTOR',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  confirm: 'SHOW_CONFIRMATION',
  error: 'ERROR',
}

class NovaProductCard extends LitElement {
  static properties = {
    url: {
      type: String,
    },
    available: {
      type: Boolean,
    },
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    description: {
      type: String,
    },
    image: {
      type: String,
      attribute: 'image',
    },
    imageAlt: {
      type: String,
    },
    secondaryImage: {
      type: String,
      attribute: 'secondary-image',
    },
    secondaryImageAlt: {
      type: String,
    },
    compareAtPrice: {
      type: String,
      attribute: 'compare-at-price',
    },
    swatches: {
      type: Array,
    },
    colourSwatchesFiles: {
      type: Object,
    },
    sizes: {
      type: Array,
    },
    price: {
      type: String,
    },
    priceVaries: {
      type: String,
      attribute: 'price-varies',
    },
    vendor: {
      type: String,
    },
    variants: {
      type: Array,
    },
    badges: {
      type: Array,
    },
    activeIndex: {
      type: Number,
    },
    options: {
      type: Array,
    },
    hideRatings: {
      attribute: 'hide-ratings',
    },
    showWishlist: {
      type: Boolean,
      attribute: 'show-wishlist',
      converter: (value) => Boolean(value),
    },
    hideSwatches: {
      state: true,
    },
    isHovered: {
      state: true,
    },
    isMobile: {
      state: true,
    },
    hasSecondOption: {
      state: true,
    },
    hasThirdOption: {
      state: true,
    },
    quickShopVariant: {
      type: Object,
      state: true,
    },
    status: {
      type: String,
      state: true,
    },
    noQuickBuy: {
      type: Boolean,
      attribute: 'no-quick-buy',
      converter: (value) => Boolean(value),
    },
    displaySwatchSlideButtons: {
      type: Boolean,
    },
    initialFirstColorSwatch: {
      type: Boolean,
    },
    fingerSizeDisplay: {
      type: Object,
    },
    fingerSizeUrl: {
      type: String,
      attribute: 'finger-size-url',
    },
    productCardType: {
      type: String,
    },
    isPdp:{
      type: String,
    },
    type: {
      type: String,
    },
    handle: {
      type: String,
    },
  }

  get activeTitle() {
    return this.swatchVariants[this.activeIndex]?.swatchTitle || false
  }

  get activeId() {
    return this.swatchVariants[this.activeIndex]?.id || this.id
  }

  get activeAvailable() {
    if (this.quickShopVariant) return this.quickShopVariant.available

    if (this.swatchVariants[this.activeIndex]) {
      return this.swatchVariants[this.activeIndex]?.available
    }
    return this.available
  }

  get activeImage() {
    let image =
      this.image ||
      this.swatchVariants[this.activeIndex]?.featured_image?.src ||
      ''

    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x480') || fragment.includes('480x')
      )

    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${
        fragments[fragments.length - 2]
      }_480x`
      image = fragments.join('.')
    }
    return `${image}&width=480&height=640`
  }

  get activeUrl() {
    const variantId = this.quickShopVariant
      ? this.quickShopVariant.id
      : this.swatchVariants[this.activeIndex]?.id
    return variantId ? `${this.url}?variant=${variantId}` : this.url
  }

  get allVariantsBySwatch() {
    const swatch = this.swatchVariants[this.activeIndex]?.swatchTitle
    return this.variants.filter((variant) => variant.options?.includes(swatch))
  }

  get activePrice() {
    if (this.quickShopVariant) return formatMoney(this.quickShopVariant.price)

    if (this.swatchVariants.length) {
      if (this.activeIndex) {
        return formatMoney(this.swatchVariants[this.activeIndex]?.price)
      }
      return (
        (this.priceVaries === 'true' ? 'From ' : '') + formatMoney(this.price)
      )
    }

    return formatMoney(this.price)
  }

  get activeCompareAtPrice() {
    const hasMultipleVariants = this.variants?.length > 1
    const activeVariant =
      this.quickShopVariant || this.swatchVariants[this.activeIndex]

    if (!activeVariant) {
      if (!this.compareAtPrice || this.compareAtPrice <= this.price) {
        return false
      }
      return formatMoney(this.compareAtPrice)
    }

    const activeCompareAtPrice = Number.parseFloat(
      activeVariant?.compare_at_price
    )
    const activePrice = Number.parseFloat(activeVariant?.price)

    if (!activeCompareAtPrice || activeCompareAtPrice <= activePrice) {
      return false
    }

    this.compareAtPrice = activeCompareAtPrice
    this.price = activePrice

    if (hasMultipleVariants && this.swatchVariants[this.activeIndex]) {
      return formatMoney(
        this.swatchVariants[this.activeIndex]?.compare_at_price
      )
    }

    if (this.compareAtPrice && this.compareAtPrice > this.price) {
      return formatMoney(this.compareAtPrice)
    }

    return false
  }

  constructor() {
    super()
    this.activeIndex
    this.variants = []
    this.swatches = []
    this.colourSwatchesFiles = {}
    this.options = []
    this.swatchVariants = []
    this.swatchesPosition = 0
    this.swatchesPerSlide = 5
    this.activeSwatchPage = 0
    this.displaySwatchSlideButtons = false
    this.isHovered = true
    this.isMobile = false
    this.hasSecondOption = false
    this.hasThirdOption = false
    this.status = quickShopState.initial
    this.hideRatings = window.Shopify.shop.includes('wholesale') // Hide ratings by default on wholesale store
    this.noQuickBuy = false

    this.renderSwatch = this.renderSwatch.bind(this)
    this.handleResize = this.handleResize.bind(this)
    this.initialFirstColorSwatch = false
    this.fingerSizeDisplay = {
      twoFingers: false,
      threeFingers: false,
      fourFingers: false,
    }
    this.fingerSizeUrl = ''
    this.productCardType = ''
    this.isPdp = ''
    this.handle = ''
    this.type = ''
    this.badges = []
  }

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('nova:resize-width', this.handleResize)
    // document.addEventListener('nova:filter-swatch-changed', (event) => {
    //   this.handleFilterSwatchChanged(event.detail)
    // })
  }

  disconnectedCallback() {
    window.removeEventListener('nova:resize-width', this.handleResize)
    // document.removeEventListener(
    //   'nova:filter-swatch-changed',
    //   this.handleFilterSwatchChanged
    // )
  }

  async firstUpdated() {
    this.setSwatchVariants()
    if (this.options?.length) {
      this.setActiveByVariantId(this.id, true)
    }

    this.handleResize()
  }

  updated(changedProperties) {
    super.updated(changedProperties)

    if (changedProperties.has('variants')) {
      this.setSwatchVariants()
    }
    if (changedProperties.has('activeIndex')) {
      this.requestUpdate()
    }

    if (changedProperties.has('activeImage')) {
      this.requestUpdate()
    }

    this.handleInitialSwatch()
  }

  handleInitialSwatch() {
    if (!this.initialFirstColorSwatch) {
      const firstButton = Array.from(
        this.shadowRoot.querySelectorAll('.js-product-card__swatch')
      ).find(
        (button) =>
          !button.classList.contains('js-product-card__swatch--sold-out')
      )

      if (firstButton) {
        firstButton.click()
        this.initialFirstColorSwatch = true
      }
    }
  }

  handleOptionSelection(e) {
    this.status = quickShopState.submit
    this.quickShopVariant = e.detail.variant
    this.hasSecondOption = true
    this.handleAddToCartClick(this.quickShopVariant)
  }

  filterNoImageSwatches(obj) {
    const newObj = { ...obj }
    for (let key in newObj) {
      if (typeof newObj[key] === 'string' && newObj[key].includes('no-image')) {
        delete newObj[key]
      }
    }
    return newObj
  }

  setSwatchVariants() {
    if (!this.swatches || !this.variants.length) return []
    let nonColorOptions
    if (this.options?.length) {
      nonColorOptions = this.options.filter(
        (option) => !this.swatches.includes(option)
      )
    }

    const matches = this.swatches.reduce((accumulator, swatch) => {
      let matchedVariants = []
      if (this.options?.length) {
        matchedVariants =
          this.variants?.filter((variant) => {
            for (const option of nonColorOptions) {
              if (!variant.options.includes(option)) return false
            }
            return variant.options.includes(swatch)
          }) || []
      } else {
        matchedVariants =
          this.variants?.filter((variant) =>
            variant.options.includes(swatch)
          ) || []
      }

      const filteredSwatchesFiles = this.filterNoImageSwatches(
        this.colourSwatchesFiles
      )

      for (const match of matchedVariants) {
        const swatchHandle = handleize(swatch)
        match.swatchTitle = swatch
        match.swatchHandle = swatchHandle
        match.swatchBackground = 'transparent'
        if (this.colourSwatchesFiles) {
          match.swatchBackground = filteredSwatchesFiles[swatchHandle]
        }
        accumulator.push(match)
      }

      return accumulator
    }, [])
    this.swatchVariants = matches
  }

  handleSwatchSlideButtonDisplay() {
    if (!this.swatches || !this.swatches.length) return
    const totalWidth =
      this.renderRoot.querySelector('.product-card').offsetWidth - 4 // 2px padding on each side
    const swatchWidth = 32 // 26px width + 6px left-margin
    const totalSwatchWidth = this.swatches.length * swatchWidth
    const display = totalSwatchWidth > totalWidth
    this.displaySwatchSlideButtons = display
  }

  handleSwatchClick(e) {
    const { variantId } = e.target.dataset
    this.quickShopVariant = null
    if (!variantId) return

    const variant = this.findVariantBySwatch(e.target.ariaLabel)
    this.quickShopVariant = variant
    this.status = quickShopState.submit
    this.setActiveByVariantId(variantId)

    if (variant.option2) {
      this.hasSecondOption = true
    }

    if (variant.option3) {
      this.hasThirdOption = true
    }
  }

  handleResize() {
    this.isMobile = window.innerWidth < 768
    this.handleSwatchSlideButtonDisplay()
  }

  handleAddToWishlist(e) {
    const { variantId } = e.target.dataset

    if (!variantId) return

    alert(variantId)
  }

  handleMouseEnter() {
    this.isHovered = true
  }

  handleMouseLeave() {
    this.isHovered = false
  }

  setActiveIndex(index) {
    this.activeIndex = index
  }

  setActiveByVariantId(id, setPage = false) {
    const variantIndex = this.swatchVariants.findIndex(
      (variant) => Number.parseInt(variant.id) === Number.parseInt(id)
    )

    if (variantIndex > -1) {
      this.setActiveIndex(variantIndex)
    }

    if (setPage) {
      const pageNumber = this.paginatedSwatches.findIndex((page) =>
        page.some((variant) => variant.id === Number.parseInt(id))
      )
      if (pageNumber > -1 && pageNumber !== this.activeSwatchPage) {
        this.setSwatchPage(pageNumber)
      }
    }
  }

  findVariantBySwatch(swatch) {
    const availableMatchedVariant = this.variants.find(
      (variant) =>
        variant.options.includes(swatch) &&
        (variant.available === true || variant.available === 'true')
    )

    if (availableMatchedVariant) return availableMatchedVariant

    return this.variants.find((variant) => variant.options.includes(swatch))
  }

  async handleAddToCartClick(e) {
    this.status = quickShopState.loading
    try {
      const variantId = e.target
        ? e.target.closest('.product-card__atc').dataset.variantId
        : e.id
      await addToCart({ id: variantId })
      this.status = quickShopState.confirm
    } catch (error) {
      console.error(error)
      this.status = quickShopState.error
    } finally {
      const quickShop = this.shadowRoot.querySelector(
        'nova-product-card-quick-shop'
      )
      quickShop.setActiveVariant(null)
      setTimeout(() => {
        this.status = quickShopState.initial
      }, 2000)
    }
  }

  handleSwatchSlide(slide, direction) {
    const slideWidth = 32 // 26px width + 6px left-margin
    const currentScrollPosition = slide.scrollLeft
    const totalWidth = slide.scrollWidth

    const newScrollPosition =
      direction === 'previous'
        ? currentScrollPosition - slideWidth > 0
          ? currentScrollPosition - slideWidth
          : 0
        : currentScrollPosition + slideWidth < totalWidth
        ? currentScrollPosition + slideWidth
        : totalWidth

    slide.scrollTo({
      left: newScrollPosition,
      behavior: 'smooth',
    })
  }

  handleSwatchSlideScroll(slide) {
    requestAnimationFrame(() => {
      const prevButton = this.shadowRoot.querySelector(
        '.js-product-card__button-previous'
      )
      const nextButton = this.shadowRoot.querySelector(
        '.js-product-card__button-next'
      )

      if (!prevButton || !nextButton) return

      const handleButtonDisplay = () => {
        if (slide.scrollLeft === 0) {
          !prevButton.classList.contains('hidden')
            ? prevButton.classList.add('hidden')
            : null
        } else if (slide.scrollWidth === slide.scrollLeft + slide.clientWidth) {
          !nextButton.classList.contains('hidden')
            ? nextButton.classList.add('hidden')
            : null
        } else {
          prevButton.classList.remove('hidden')
          nextButton.classList.remove('hidden')
        }
      }

      handleButtonDisplay()

      slide.addEventListener('scroll', () => {
        handleButtonDisplay()
      })
    })
  }

  resetFingerSizeValues() {
    this.fingerSizeDisplay.twoFingers = false
    this.fingerSizeDisplay.threeFingers = false
    this.fingerSizeDisplay.fourFingers = false
  }

  handleFingerSizeValues() {
    this.resetFingerSizeValues()
    if (this.swatchVariants[this.activeIndex]) {
      for (const variant of this.variants) {
        if (variant.option1 === this.swatchVariants[this.activeIndex].option1) {
          if (!variant.metafields || !variant.metafields['as-wfh']) return
          if (
            variant.metafields['as-wfh'].finger_size_badge_2?.toString() ===
            'true'
          ) {
            this.fingerSizeDisplay.twoFingers = true
          }
          if (
            variant.metafields['as-wfh'].finger_size_badge_3?.toString() ===
            'true'
          ) {
            this.fingerSizeDisplay.threeFingers = true
          }
          if (
            variant.metafields['as-wfh'].finger_size_badge_4?.toString() ===
            'true'
          ) {
            this.fingerSizeDisplay.fourFingers = true
          }
        }
      }
    } else {
      const firstVariant = this.variants[0]
      if (!firstVariant || !firstVariant.metafields || !firstVariant.metafields['as-wfh']) return
      if (
        firstVariant.metafields['as-wfh'].finger_size_badge_2?.toString() ===
        'true'
      ) {
        this.fingerSizeDisplay.twoFingers = true
      }
      if (
        firstVariant.metafields['as-wfh'].finger_size_badge_3?.toString() ===
        'true'
      ) {
        this.fingerSizeDisplay.threeFingers = true
      }
      if (
        firstVariant.metafields['as-wfh'].finger_size_badge_4?.toString() ===
        'true'
      ) {
        this.fingerSizeDisplay.fourFingers = true
      }
    }
  }

  renderFingerSize() {
    if (this.productCardType === 'mini') return

    this.handleFingerSizeValues()

    return html`
      <div class="product-card__finger-size-container flex flex-column">
        ${this.fingerSizeDisplay.twoFingers
          ? html`<div class="product-card__finger-size-badge">
            <a
              href="${this.fingerSizeUrl}"
              class="product-card__finger-size-badge-link flex flex-align-center"
              tabindex="-1"
            >
              <p>SMALL</p>
              ${paths.twoFingerSize}
            </a>
          </div>`
          : ''}
        ${this.fingerSizeDisplay.threeFingers
          ? html`<div
            class="product-card__finger-size-badge flex flex-align-center"
            tabindex="-1"
          >
            <a
              href="${this.fingerSizeUrl}"
              class="product-card__finger-size-badge-link flex flex-align-center"
              tabindex="-1"
            >
              <p>MEDIUM</p>
              ${paths.threeFingerSize}
            </a>
          </div>`
          : ''}
        ${this.fingerSizeDisplay.fourFingers
          ? html`<div
            class="product-card__finger-size-badge flex flex-align-center"
          >
            <a
              href="${this.fingerSizeUrl}"
              class="product-card__finger-size-badge-link flex flex-align-center"
            >
              <p>LARGE</p>
              ${paths.fourFingerSize}
            </a>
          </div>`
          : ''}
      </div>
    `
  }

  renderMedia() {
    return html`
      <div
        class="product-card__media ${classMap({
      'product-card__media--enable-hover': this.secondaryImage,
    })}"
      >
        ${this.image
      ? html` <div
            class="product-card__image product-card__image--primary
            ${this.productCardType === 'mini' ? 'product-card__image-mini' : ''}
            ${this.isPdp === 'true' ? 'is-pdp' : ''}"
          >
              <a href="${this.activeUrl}">
                <img src=${this.activeImage} alt=${this.title} loading="lazy" />
              </a>
            </div>`
      : ''}
        ${this.secondaryImage
      ? html` <div
              class="product-card__image product-card__image--secondary
              ${this.productCardType === 'mini' ? ' product-card__image-mini' : ''}
              ${this.isPdp === 'true' ? 'is-pdp' : ''}"
            >
              <a href="${this.activeUrl}">
                <img
                  src=${this.secondaryImage}
                  alt=${this.title}
                  loading="lazy"
                />
              </a>
            </div>`
      : ''}
        ${this.renderBadges()}
        ${this.renderFingerSize()} ${this.renderQuickShopCTAs()}

        <slot name="wishlist-button"></slot>
      </div>
    `
  }

  renderBadges() {
    if (
      !this.badges ||
      this.badges.length === 0 ||
      this.productCardType === 'mini'
    )
      return null
    const badgesHtml = this.badges.map((badge) => {
      const badgeData = badge.split(',')
      const badgeLabel = badgeData[0]
      const badgeColour = badgeData[1]
      const badgeBackgroundColour = badgeData[2]
      const styleVars = {}

      if (badgeColour) styleVars['--badge-color'] = badgeColour
      if (badgeBackgroundColour)
        styleVars['--badge-bg-color'] = badgeBackgroundColour

      return html`
        <span class="badges__item caption" style="${styleMap(styleVars)}">
          ${badgeLabel ? badgeLabel : ''}
        </span>
      `
    })

    return html`<div class="badges">${badgesHtml}</div>`
  }

  renderPrice() {
    return html`
      <span class="product-card__price caption flex">
        <div
          class="price ${classMap({
            'price--on-sale': this.activeCompareAtPrice,
          })}"
        >
          <dl>
            <div class="price__regular">
              <dt>
                <span class="visually-hidden visually-hidden--inline"
                >Regular price</span
                >
              </dt>
              <dd>
                <span class="price-item price-item--regular"
                >${this.activePrice}</span
                >
              </dd>
            </div>

            ${this.activeCompareAtPrice
              ? html`
                <div class="price__sale">
                  <dt>
                      <span class="visually-hidden visually-hidden--inline"
                      >Sale price</span
                      >
                  </dt>
                  <dd>
                      <span class="price-item price-item--sale">
                        ${this.activeCompareAtPrice}
                      </span>
                  </dd>
                  <dt class="price__compare">
                      <span class="visually-hidden visually-hidden--inline"
                      >Regular price</span
                      >
                  </dt>
                  <dd class="price__compare">
                    <s class="price-item price-item--regular">
                      ${this.activePrice}
                    </s>
                  </dd>
                </div>
              `
              : ''}
          </dl>
        </div>
      </span>
    `
  }

  getBackgroundColor(swatch) {
    const swatchHandle = swatch.toLowerCase().replace(/ /g, '-')
    const swatchFileUrl = this.colourSwatchesFiles[swatchHandle]
    if (swatchFileUrl) {
      return `background-image:url(${swatchFileUrl})`
    }

    return null
  }

  renderSwatch(swatch) {
    if (this.noQuickBuy) {
      return html` <button
        class="product-card__swatch"
        style="background-color: ${swatch}; ${this.getBackgroundColor(swatch)};"
        aria-label="${swatch}"
      ></button>`
    }

    const variant = this.findVariantBySwatch(swatch)

    const stateClasses = {
      'product-card__swatch--active': variant.id === this.activeId,
      'js-product-card__swatch--sold-out': !variant.available,
    }

    return html`
      <button
        class="product-card__swatch js-product-card__swatch ${classMap(
          stateClasses
        )}"
        style="background-color: ${swatch}; ${this.getBackgroundColor(swatch)};"
        data-variant-id="${variant.id}"
        aria-label="${swatch}"
        @click="${this.handleSwatchClick}"
      ></button>
    `
  }

  renderSwatchSlideButtons(direction) {
    const slide = this.shadowRoot.querySelector(
      '.js-product-card__swatch-container'
    )

    if (!this.displaySwatchSlideButtons || !slide) return

    return html`
      <button
        class="js-product-card__button-${direction} product-card__button-${direction} product-card__button flex flex-justify-center flex-align-center${this
          .productCardType === 'mini'
          ? ' product-card__button-mini'
          : ''}"
        aria-label="${direction} swatch"
        @click="${() => this.handleSwatchSlide(slide, direction)}"
      >
        ${paths.carouselCaret} ${this.handleSwatchSlideScroll(slide)}
      </button>
    `
  }

  renderProductSwatches() {
    if (!this.swatches || this.swatches.length === 0) return

    return html`
      <sl-resize-observer @sl-resize=${this.handleResize}>
        <div
          class="product-card__swatch-container js-product-card__swatch-container"
        >
          <div class="product-card__swatch-slide">
            ${html` ${this.swatches.map(this.renderSwatch)} `}
          </div>
        </div>
      </sl-resize-observer>
    `
  }

  renderQuickShopCTAs() {
    if (this.variants.length === 1) this.quickShopVariant = this.variants[0]

    if (!this.isHovered || !this.quickShopVariant || this.hasSecondOption)
      return null
    if (
      this.status === quickShopState.submit ||
      this.status === quickShopState.loading ||
      this.status === quickShopState.initial
    ) {
      return html`
        <sl-button
          variant="primary"
          class="product-card__atc button"
          ?disabled=${this.quickShopVariant.available !== true}
          data-variant-id="${this.quickShopVariant.id}"
          ?loading=${this.status === quickShopState.loading}
          @click="${this.handleAddToCartClick}"
        >
          ${paths.shop}
        </sl-button>
      `
    }

    if (this.status === quickShopState.error) {
      return html`
        <div
          class="product-card__atc product-card__atc--confirm product-card__atc--error"
        >
          <span>Something Went Wrong</span>
        </div>
      `
    }

    return null
  }

  renderRating() {
    return html`
      <!-- Stamped - Begin Star Rating Badge -->
      <span
        class="stamped-product-reviews-badge stamped-main-badge"
        data-id="${this.id}"
        data-product-sku="${this.handle}"
        data-product-type="${this.type}"
        data-product-title="${this.title}"
        style="display:block;"
      >
      </span>
      <!-- Stamped - End Star Rating Badge -->
    `
  }

  render() {
    return html`
      <sl-card
        id="product-card-${this.id}"
        class="product-card"
        style="--padding: 0; --border-width: 0; --sl-shadow-x-small: 0;"
        @mouseenter="${this.handleMouseEnter}"
        @mouseleave="${this.handleMouseLeave}"
        @nova:option-selected="${this.handleOptionSelection}"
      >
        ${this.renderMedia()}

        <div class="product-card__content">
          <a href="${this.activeUrl}" class="product-card__content-link">
            <header class="product-card__header">
              <slot name="stamped-badge"></slot>
              <h2 class="h5 product-card__title">${this.title}</h2>

              <nova-price-display
                class="product-card__price"
                .price="${this.activePrice}"
                .compareAtPrice="${this.activeCompareAtPrice}"
              >
              </nova-price-display>
            </header>
            ${this.renderRating()}
          </a>
          ${this.hasThirdOption !== true && this.sizes
      ? html`<nova-product-card-quick-shop
              product-options=${this.sizes}
              productCardType=${this.productCardType}
              .swatches="${this.swatches}"
              .active-variant="${this.swatchVariants[this.activeIndex]}"
              .variants="${this.swatchVariants.length !== 0
        ? this.allVariantsBySwatch
        : this.variants}"
            >
            </nova-product-card-quick-shop>`
      : ''}
          <div class="product-card__swatches flex">
            ${this.renderSwatchSlideButtons('previous')}
            ${this.renderProductSwatches()}
            ${this.renderSwatchSlideButtons('next')}
          </div>
        </div>
      </sl-card>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.helpers,
    styles.typography,
    styles.skeleton,
    css`
      :host {
        display: flex;
        container-type: inline-size;
        width: 100%;
        position: relative;
      }

      .product-card::part(base) {
        width: 100%;
      }

      .product-card::part(body) {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-lg, 16px);
      }

      .product-card__content {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-md, 12px);
      }

      .product-card__header {
        display: flex;
        flex-direction: column;
        align-items: start;
        justify-content: space-between;
        gap: var(--nova-space-xs, 8px);
      }

      .product-card__description {
        font-size: 14px;
        margin: 0;
        line-height: 120%;
        color: #6b6b6b;
        font-family: var(--nova-font-body-regular);
      }

      @media screen and (min-width: 768px) {
        .product-card__description {
          font-size: 16px;
        }
      }

      .product-card__media {
        position: relative;
      }

      nova-product-card-quick-shop {
        display: block;
        bottom: var(--nova-space-xs);
        width: 100%;
        z-index: 2;
      }

      @media screen and (min-width: 990px) {
        nova-product-card-quick-shop {
          bottom: var(--nova-space-xs);
          left: var(--nova-space-xs);
          right: var(--nova-space-xs);
          width: auto;
        }
      }

      .product-card__image {
        width: 100%;
        aspect-ratio: 0.8;
        border-radius: 10px;
        background: linear-gradient(
          150.38deg,
          rgb(222, 222, 218) 3.74%,
          rgba(222, 222, 218, 0.49) 63.73%,
          rgba(222, 222, 218, 0) 85.34%
        );
        transition: opacity 0.3s ease-in-out;
      }

      .product-card__image-mini.product-card__image {
        aspect-ratio: 1;
      }

      .product-card__image img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        border-radius: 10px;
      }

      .product-card__image--primary {
        opacity: 1;
      }

      .product-card__image--secondary {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
      }

    @container (min-width: 300px) {
      .product-card__media--enable-hover:hover .product-card__image--primary {
        opacity: 0;
        position: absolute;
        top: 0;
        left: 0;
      }

      .product-card__media--enable-hover:hover
      .product-card__image--secondary {
        opacity: 1;
        position: relative;
      }
    }

      .product-card__title {
        margin: 0;
        font-weight: 600;
        font-family: var(--nova-font-body-regular);
      }

      .product-card__price {
        font-size: 14px;
        font-family: var(--nova-font-body-regular);
        line-height: 120%;
        color: #000000;
        flex: 0;
      }

      @media screen and (min-width: 768px) {
        .product-card__price {
          font-size: 16px;
        }
      }

      .product-card__price dd {
        margin: 0;
      }

      .product-card__swatch-slide {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        height: 30px;
        width: fit-content;
      }

      .product-card__swatch {
        width: 26px;
        height: 26px;
        border: 1px solid var(--nova-utility-grey-1);
        border-radius: 3px;
        text-indent: -9999px;
        position: relative;
      }

      .product-card__swatch + .product-card__swatch {
        margin-left: 6px;
      }

      .product-card__swatch--active::before {
        content: '';
        box-shadow: 0 0 0 1px var(--nova-brand-primary);
        position: absolute;
        border-radius: 4px;
        width: 28px;
        height: 28px;
        transform: translate(-2px, -2px);
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
      }

      .js-product-card__swatch--sold-out {
        cursor: not-allowed;
      }

      .js-product-card__swatch--sold-out::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        background: linear-gradient(
          to bottom left,
          transparent 48%,
          var(--nova-brand-ink) 48%,
          var(--nova-brand-ink) 52%,
          transparent 52%
        );
        transform: rotate(0deg);
      }

      .product-card__button {
        height: 30px;
        width: 32px;
        background-color: var(--nova-brand-secondary, #ffffff);
        padding: 0 6px 0 0;
        margin-left: 2px;
      }

      .product-card__button svg {
        width: 26px;
        height: 26px;
      }

      .product-card__button-next {
        transform: rotate(180deg);
      }

      @media screen and (min-width: 768px) {
        .product-card__button {
          width: 16px;
        }

        .product-card__button-mini.product-card__button {
          width: 32px;
        }

        .product-card__button svg {
          width: 10px;
          height: 10px;
        }

        .product-card__button-mini.product-card__button svg {
          width: 26px;
          height: 26px;
        }
      }

      .product-card__swatch-container {
        overflow-y: hidden;
        -ms-overflow-style: none;
        scrollbar-width: none;
        padding: 0 2px;
      }

      .product-card__swatch-container::-webkit-scrollbar {
        display: none;
      }

      .product-card__wishlist {
        position: absolute;
        right: 0;
        top: var(--nova-space-sm);
      }

      .product-card__wishlist svg {
        pointer-events: none;
      }

      .product-card__wishlist:hover svg {
        fill: var(--nova-brand-secondary);
      }

      .product-card__wishlist:active svg {
        fill: var(--nova-brand-ink);
      }

      .product-card .badges {
        position: absolute;
        top: var(--nova-space-xs);
        left: var(--nova-space-xs);
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        width: calc(100% - 50px);
        gap: var(--nova-space-2xs);
      }

      @media screen and (min-width: 750px) {
        .product-card .badges {
          flex-direction: row;
        }
      }

      .badges__item {
        align-items: flex-start;
        border-radius: var(--nova-border-radius-s, 4px);
        background: var(--badge-bg-color, #abc4ca);
        color: var(--badge-color, --nova-brand-ink);
        display: flex;
        font-size: 12px;
        letter-spacing: 1.4px;
        padding: 6px var(--nova-space-md);
        text-transform: uppercase;
        pointer-events: none;
        border-radius: 44px;
        width: fit-content;
        text-align: center;
      }

      sl-button[variant='primary:ink'] {
        width: 100%;
      }

      sl-button[variant='primary:ink']::part(base) {
        transition: 0.15s;
      }
      sl-button[variant='primary:ink']::part(base):hover {
        background-color: transparent;
      }
      sl-button[variant='primary:ink'] {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      }

      sl-button[variant='primary:ink']::part(base) {
        background-color: var(--nova-brand-ink);
        border: none;
        color: white;
      }

      sl-button[variant='primary:ink']::part(base):hover {
        color: white;
        background-color: var(--nova-brand-primary);
      }

      sl-button[disabled]::part(base):hover {
        background-color: var(--nova-brand-neutral);
      }

      .product-card__atc {
        width: 40px;
        height: 40px;
        position: absolute;
        bottom: var(--nova-space-xs);
        left: var(--nova-space-xs);
        right: 0;
      }

      .product-card__atc span {
        line-height: 40px;
      }

      .product-card__atc::part(base):hover {
        border: none;
        background-color: var(--nova-brand-reverse);
      }

      .product-card__atc[disabled]::part(base) {
        background-color: var(--nova-brand-neutral);
        border-color: var(--nova-brand-neutral);
        opacity: 1;
      }

      .product-card__atc::part(label) {
        display: flex;
        align-items: center;
      }

      .product-card__atc--confirm {
        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        background-color: white;
        border-radius: var(--nova-button-radius);
        background-color: var(--nova-brand-secondary);
      }

      .product-card__atc--error {
        background-color: var(--nova-utility-error);
        color: #fff;
      }

      sl-card {
        width: 100%;
      }

      .product-card__finger-size-container {
        position: absolute;
        /* Move finger badge above the quick shop button (40px height) */
        bottom: calc(var(--nova-space-xs) + 40px);
        right: var(--nova-space-xs);
        gap: var(--nova-space-2xs, 4px);
      }

      .product-card__finger-size-badge-link {
        justify-content: space-between;
        gap: var(--nova-space-2xs, 4px);
        border-radius: 44px;
        background-color: var(--nova-brand-secondary, #ffffff);
        padding: 6px var(--nova-space-md, 12px);
      }

      .product-card__finger-size-badge p {
        font-size: 12px;
        line-height: 120%;
        margin: 0;
        color: var(--nova-brand-primary, #5812CD);
      }

      .is-pdp {
        max-height: 230px;
      }
    `,
  ]
}

customElements.define('nova-product-card', NovaProductCard)
